import * as api from "../generated-api";
import { apiCall, apiCallAsync, apiCallWithResponse } from "../common";
import { AxiosResponse } from "axios";
import { isEmpty } from "../../helpers/utilsTS";

export const getDealDocuments = (
  orgId: number,
  dealId: number,
  successCallback?: (dealDocuments: api.DealDocument[]) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<api.DealDocument[]>(
    () => {
      return api.getDealDocuments(orgId, dealId);
    },
    successCallback,
    errorCallback
  );
};

export const filenameWithoutExtension = (fileName: string) => {
  const fileNameParts = fileName.split(".");
  return fileNameParts.slice(0, fileNameParts.length - 1).join(".");
};

export const fileExtension = (fileName: string) => {
  const fileNameParts = fileName.split(".");
  return fileNameParts[fileNameParts.length - 1];
};

export const createDealDocument = (
  orgId: number,
  dealId: number,
  file: File,
  type: api.DealDocumentType,
  successCallback?: (dealDocument: api.DealDocument) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<api.DealDocument>(
    () => {
      return api.createDealDocument(orgId, dealId, {
        name: filenameWithoutExtension(file.name),
        extension: fileExtension(file.name),
        documentType: type,
        file,
      });
    },
    successCallback,
    errorCallback
  );
};

export const asyncCreateDealDocument = async (
  orgId: number,
  dealId: number,
  file: File,
  type: api.DealDocumentType,
  name?: string
): Promise<AxiosResponse> => {
  return await api.createDealDocument(orgId, dealId, {
    name: isEmpty(name) ? filenameWithoutExtension(file.name) : name,
    extension: fileExtension(file.name),
    documentType: type,
    file,
  });
};

export const asyncCreateDealDocumentVersion = async (
  orgId: number,
  dealId: number,
  documentId: number,
  file: File
): Promise<AxiosResponse> => {
  return await api.createDealDocumentVersion(orgId, dealId, documentId, {
    extension: fileExtension(file.name),
    file,
  });
};

export const getDealDocument = (
  orgId: number,
  dealId: number,
  documentId: number,
  successCallback?: (dealDocument: api.DealDocument) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<api.DealDocument>(
    () => {
      return api.getDealDocument(orgId, dealId, documentId);
    },
    successCallback,
    errorCallback
  );
};

export const getDealDocumentContents = (
  orgId: number,
  dealId: number,
  documentId: number,
  successCallback: (response: AxiosResponse<Blob>) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCallWithResponse(
    () => {
      return api.getDealDocumentContents(orgId, dealId, documentId, {
        responseType: "blob",
      });
    },
    successCallback,
    errorCallback
  );
};

export const updateDealDocument = (
  orgId: number,
  dealId: number,
  documentId: number,
  updateData: api.DealDocumentUpdate,
  successCallback?: (dealDocument: api.DealDocument) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<api.DealDocument>(
    () => {
      return api.updateDealDocument(orgId, dealId, documentId, updateData);
    },
    successCallback,
    errorCallback
  );
};

export const deleteDealDocument = (
  orgId: number,
  dealId: number,
  documentId: number,
  successCallback?: () => void,
  errorCallback?: (error: Error) => void
) => {
  apiCall<true>(
    () => {
      return api.deleteDealDocument(orgId, dealId, documentId);
    },
    successCallback,
    errorCallback
  );
};

export const getDocumentWithSignedSignaturePages = (
  orgId: number,
  dealId: number,
  documentId: number,
  successCallback: (response: AxiosResponse) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCallWithResponse(
    () => {
      return api.getDocumentWithSignedSignaturePages(
        orgId,
        dealId,
        documentId,
        {
          responseType: "blob",
        }
      );
    },
    successCallback,
    errorCallback
  );
};

export const getDealDocumentSignatureSignaturePage = async (
  orgId: number,
  dealId: number,
  documentId: number,
  dealDocumentSignerId: number,
  errorCallback?: (error: Error) => void
) => {
  return await apiCallAsync<Buffer>(() => {
    return api.getDealDocumentSignatureSignaturePage(
      orgId,
      dealId,
      documentId,
      dealDocumentSignerId,
      { responseType: "blob" }
    );
  }, errorCallback);
};

export const getScheduleOfPurchasers = (
  orgId: number,
  dealId: number,
  successCallback: (response: AxiosResponse) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCallWithResponse(
    () => {
      return api.getScheduleOfPurchasers(orgId, dealId, {
        responseType: "blob",
      });
    },
    successCallback,
    errorCallback
  );
};

export const getDealDocumentDownloadForVersion = (
  orgId: number,
  dealId: number,
  dealDocumentId: number,
  dealDocumentVersionNumber: number,
  format: api.DealDocumentVersionDownloadFormats,
  successCallback: (response: AxiosResponse) => void,
  errorCallback?: (error: Error) => void
) => {
  apiCallWithResponse(
    () => {
      return api.getDealDocumentDownloadForVersion(
        orgId,
        dealId,
        dealDocumentId,
        dealDocumentVersionNumber,
        format,
        {
          responseType: "blob",
        }
      );
    },
    successCallback,
    errorCallback
  );
};

export const processDownload = (
  response: AxiosResponse,
  defaultName: string
) => {
  const fileName = decodeURI(
    response.headers["x-suggested-filename"] || defaultName
  );

  // create file link in browser's memory
  const href = URL.createObjectURL(response.data);

  // create "a" HTML element with href to file & click
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", fileName); //or any other extension
  document.body.appendChild(link);
  link.click();

  // clean up "a" element & remove ObjectURL
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

export const downloadExecutedFile = (
  orgId: number,
  dealId: number,
  documentId: number
) => {
  getDocumentWithSignedSignaturePages(orgId, dealId, documentId, (response) => {
    processDownload(response, "executed-document.pdf");
  });
};

export const downloadScheduleOfPurchasers = (orgId: number, dealId: number) => {
  getScheduleOfPurchasers(orgId, dealId, (response) => {
    processDownload(response, "schedule-of-purchasers.pdf");
  });
};

export const downloadDealDocumentVersion = (
  orgId: number,
  dealId: number,
  dealDocumentId: number,
  dealDocumentVersionNumber: number,
  format: api.DealDocumentVersionDownloadFormats
) => {
  getDealDocumentDownloadForVersion(
    orgId,
    dealId,
    dealDocumentId,
    dealDocumentVersionNumber,
    format,
    (response) => {
      const fileName = response.headers["x-suggested-filename"] as string;
      const decodedFileName = decodeURI(fileName);

      processDownload(response, decodedFileName);
    }
  );
};
